import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { IconX } from "@tabler/icons-react";
import { useAuth } from "@/auth";
import { useOnboarding } from "@/lib/onboarding";
import { greetingName } from "./FirstRun";
import { testIds } from "@/testIds";

const T = testIds.tour;

/** Test builds: the e2e suite marks the old coach as seen, which now also means the tour. */
const E2E_SEEN_KEY = "lilia-editor-coach-v1";

/** Ask the editor's tour to start again from step 1 (Help → Take the tour). */
export const TOUR_START_EVENT = "lilia:tour-start";
export const requestTour = () => window.dispatchEvent(new CustomEvent(TOUR_START_EVENT));

interface Step {
  key: string;
  /** The control it points at. */
  anchor: string;
  title: string;
  body: string;
}

/**
 * The tour (5d, Olivia 14 Oct 2026): Blocks, Ask Lilia, Validate, Export, each card beside its real
 * control. No scrim, so the page stays usable; typing ends it. Shown once per account (the API's
 * TourSeenAt), and again from Help → Take the tour. Someone who may only read gets the Ask Lilia
 * step alone (Olivia, 21 Oct). reports/2026-10-21-welcome-onboarding.md
 */
export function Tour({ mayEdit, role, ownerName }: { mayEdit: boolean; role?: string | null; ownerName?: string | null }) {
  const { t } = useTranslation("editor");
  const { user } = useAuth();
  // 2a (Olivia, 21 Oct): someone invited here meets the document with a line naming who asked.
  const invited = !!role && role !== "owner";
  const [welcomeClosed, setWelcomeClosed] = useState(false);
  const { onboarding, loaded, record } = useOnboarding();
  const [params, setParams] = useSearchParams();
  const [running, setRunning] = useState(false);
  const [index, setIndex] = useState(0);

  const steps = useMemo<Step[]>(
    () =>
      mayEdit
        ? [
            { key: "blocks", anchor: '[data-testid="flow-editor"]', title: t("tour.blocksTitle"), body: t("tour.blocksBody") },
            { key: "ask", anchor: '[data-testid="ask-lilia-open"]', title: t("tour.askTitle"), body: t("tour.askBody") },
            { key: "validate", anchor: '[data-testid="panel-rail-item"][data-panel="validate"]', title: t("tour.validateTitle"), body: t("tour.validateBody") },
            { key: "export", anchor: '[data-testid="doc-export"]', title: t("tour.exportTitle"), body: t("tour.exportBody") },
          ]
        : [{ key: "ask", anchor: '[data-testid="ask-lilia-open"]', title: t("tour.readerAskTitle"), body: t("tour.readerAskBody") }],
    [mayEdit, t],
  );

  const start = useCallback(() => {
    setIndex(0);
    setRunning(true);
  }, []);

  // Once per account: the first document opened after sign-up. Once per mount too: each step
  // recorded refreshes the onboarding record, which must not start it over.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current || !loaded || !onboarding || onboarding.tourSeen || onboarding.legacy) return;
    if (import.meta.env.VITE_E2E) {
      try { if (localStorage.getItem(E2E_SEEN_KEY) === "1") return; } catch { /* storage denied */ }
    }
    autoStarted.current = true;
    start();
  }, [loaded, onboarding, start]);

  // Help → Take the tour: here (an event), or arriving from elsewhere (?tour=1).
  useEffect(() => {
    const again = () => {
      record({ step: "tour-restart" }).catch(() => undefined);
      start();
    };
    window.addEventListener(TOUR_START_EVENT, again);
    if (params.get("tour") === "1") {
      params.delete("tour");
      setParams(params, { replace: true });
      again();
    }
    return () => window.removeEventListener(TOUR_START_EVENT, again);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the URL is read once on arrival
  }, []);

  const finish = useCallback(() => {
    setRunning(false);
    record({ step: "tour-seen" }).catch(() => undefined);
  }, [record]);

  useEffect(() => {
    if (running) record({ step: "tour-step", body: { step: index + 1 } }).catch(() => undefined);
  }, [running, index, record]);

  // Typing ends it: the page is theirs (5d).
  useEffect(() => {
    if (!running) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey)) {
        const inCard = (e.target as HTMLElement | null)?.closest?.(`[data-testid="${T.step}"]`);
        if (!inCard) finish();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [running, finish]);

  const step = steps[Math.min(index, steps.length - 1)];
  const place = useAnchorPlace(running ? step.anchor : null);

  if (!running) return null;
  const last = index >= steps.length - 1;
  const name = greetingName(user?.userName);

  return createPortal(
    <>
    {invited && !welcomeClosed && (
      <div
        role="status"
        data-testid={T.invitedWelcome}
        className="fixed left-1/2 top-[60px] z-[290] flex w-[min(720px,calc(100vw-32px))] -translate-x-1/2 items-center gap-3 rounded-[12px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)] px-4 py-2.5 text-[13.5px] shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
      >
        <span aria-hidden className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--lilia-bg-muted)] text-[12px] font-semibold text-[var(--lilia-text)]">
          {(ownerName ?? "·").trim().charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1 text-[var(--lilia-text-secondary)]">
          <strong className="text-[var(--lilia-text)]">{name ? t("tour.invited.welcome", { name }) : t("tour.invited.welcomeNoName")}</strong>{" "}
          {ownerName ? t(mayEdit ? "tour.invited.invitedEdit" : "tour.invited.invitedView", { inviter: ownerName }) : null}
        </span>
        <button type="button" data-testid={T.invitedWelcomeClose} aria-label={t("tour.invited.close")} onClick={() => setWelcomeClosed(true)}
          className="flex h-7 w-7 items-center justify-center rounded text-[var(--lilia-text-secondary)] hover:bg-[var(--lilia-hover)]">
          <IconX size={15} />
        </button>
      </div>
    )}
    <div
      role="dialog"
      aria-modal="false"
      aria-label={step.title}
      data-testid={T.step}
      data-step={step.key}
      className="fixed z-[300] flex w-[340px] max-w-[calc(100vw-32px)] flex-col gap-2 rounded-[14px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)] p-[18px] shadow-[0_18px_48px_rgba(0,0,0,0.18)]"
      style={place}
    >
      <span className="text-[12px] font-semibold text-[var(--lilia-text-secondary)]">{t("tour.of", { n: index + 1, total: steps.length })}</span>
      <strong className="text-[16px] text-[var(--lilia-text)]">{step.title}</strong>
      <p className="m-0 text-[13.5px] leading-[1.55] text-[var(--lilia-text-secondary)]">{step.body}</p>
      <div className="mt-2 flex items-center gap-2">
        <button type="button" data-testid={T.skip} onClick={finish} className="mr-auto text-[13px] text-[var(--lilia-text-secondary)] hover:text-[var(--lilia-text)]">
          {t("tour.skip")}
        </button>
        {index > 0 && (
          <button type="button" data-testid={T.back} onClick={() => setIndex((i) => i - 1)}
            className="h-8 rounded-lg border border-[var(--lilia-border-strong)] px-3 text-[13px] font-semibold text-[var(--lilia-text)]">
            {t("tour.back")}
          </button>
        )}
        <button type="button" data-testid={T.next} onClick={() => (last ? finish() : setIndex((i) => i + 1))}
          className="h-8 rounded-lg bg-[var(--lilia-primary)] px-3.5 text-[13px] font-semibold text-[color:var(--lilia-on-primary)]">
          {last ? t("tour.done") : t("tour.next")}
        </button>
      </div>
    </div>
    </>,
    document.body,
  );
}

/** Where the card sits: beside its control when it is on screen, else low in the middle. */
function useAnchorPlace(selector: string | null): React.CSSProperties {
  const [place, setPlace] = useState<React.CSSProperties>({ left: "50%", bottom: 40, transform: "translateX(-50%)" });
  useLayoutEffect(() => {
    if (!selector) return;
    const measure = () => {
      const el = document.querySelector(selector);
      const r = el?.getBoundingClientRect();
      if (!r || r.width === 0) {
        setPlace({ left: "50%", bottom: 40, transform: "translateX(-50%)" });
        return;
      }
      const W = 340, gap = 12, vw = window.innerWidth, vh = window.innerHeight;
      // A big target (the page) gets the card inside its top edge; a control gets it below or beside.
      if (r.height > vh / 2) {
        setPlace({ top: Math.max(r.top, 0) + 72, left: Math.min(Math.max(r.left + r.width / 2 - W / 2, 16), vw - W - 16) });
      } else if (r.bottom + gap + 200 < vh) {
        setPlace({ top: r.bottom + gap, left: Math.min(Math.max(r.left + r.width / 2 - W / 2, 16), vw - W - 16) });
      } else {
        setPlace({ top: Math.max(r.top - 220, 16), left: Math.max(r.left - W - gap, 16) });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    const id = window.setInterval(measure, 500);
    return () => { window.removeEventListener("resize", measure); window.clearInterval(id); };
  }, [selector]);
  return place;
}
